import { useEffect, useState } from "react";
import {
  Control,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
} from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import { MiControlledTextField } from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { IMaterialsResponse } from "@miplatform/types";
import { Box, Typography } from "@mui/material";

interface MiControlledTextFieldProps {
  name?: string;
  formControl?: object;
  rules?: object;
  type?: string;
  [key: string]: any;
}

function parseInputValue(raw: string): number | null {
  const parsed = Number.parseFloat(raw.replace(/[^0-9.-]/g, ""));
  return Number.isNaN(parsed) ? null : parsed;
}

function computeFromNetSalesPrice(netSalesPrice: number, cost: number) {
  const marginDollar = netSalesPrice - cost;
  const marginPercent =
    netSalesPrice !== 0 ? (marginDollar / netSalesPrice) * 100 : 0;
  return {
    marginDollar: Number(marginDollar.toFixed(2)),
    marginPercent: Number(marginPercent.toFixed(2)),
  };
}

function computeFromMarginPercent(marginPercent: number, cost: number) {
  const divisor = 1 - marginPercent / 100;
  if (divisor === 0) {
    return { netSalesPrice: 0, marginDollar: -cost };
  }
  const netSalesPrice = cost / divisor;
  const marginDollar = netSalesPrice - cost;
  return {
    netSalesPrice: Number(netSalesPrice.toFixed(2)),
    marginDollar: Number(marginDollar.toFixed(2)),
  };
}

function computeFromMarginDollar(marginDollar: number, cost: number) {
  const netSalesPrice = cost + marginDollar;
  const marginPercent =
    netSalesPrice !== 0 ? (marginDollar / netSalesPrice) * 100 : 0;
  return {
    netSalesPrice: Number(netSalesPrice.toFixed(2)),
    marginPercent: Number(marginPercent.toFixed(2)),
  };
}

export function MaterialPricingRow<T extends FieldValues>({
  material,
  watched,
  modifierValue,
  valueType,
  basePath,
  formControl,
  setValue,
  disabled,
  search = "",
}: Readonly<MaterialPricingRowProps<T>>) {
  const { classes } = useStyles();
  const [editingField, setEditingField] = useState<string | null>(null);

  const isMultiplier = valueType === "multiplier";

  const primaryLabel = isMultiplier ? "List" : "Cost";
  const materialLabel = `${material.manufacturerPartNumber} | ${material.category.itemDescription}`;
  const modifierLabel = isMultiplier ? "Multiplier" : "Markup (%)";

  const listPrice = material.manufacturerListPrice ?? 0;
  const cost = material.cost ?? listPrice;
  const preTaxSalePrice =
    listPrice *
    (isMultiplier ? modifierValue ?? 1 : 1 + (modifierValue ?? 0) / 100);
  const taxRate = watched?.taxRate ?? material.taxRate ?? 0;
  const afterTaxSalePrice = preTaxSalePrice * (1 + taxRate / 100);

  const netSalesPrice =
    watched?.netSalesPrice !== undefined
      ? watched.netSalesPrice
      : material.netSalesPrice;
  const initialMargins =
    material.netSalesPrice != null
      ? computeFromNetSalesPrice(material.netSalesPrice, cost)
      : null;
  const marginPercent =
    watched?.marginPercent !== undefined
      ? watched.marginPercent
      : material.marginPercent ?? initialMargins?.marginPercent ?? null;
  const marginDollar =
    watched?.marginDollar !== undefined
      ? watched.marginDollar
      : material.marginDollar ?? initialMargins?.marginDollar ?? null;

  const setField = (field: string, value: number | null) =>
    setValue(
      `${basePath}.${field}` as Path<T>,
      value as PathValue<T, Path<T>>,
      { shouldValidate: true, shouldDirty: true }
    );

  const handleNetSalesPriceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (editingField !== "netSalesPrice") return;
    setEditingField(null);

    const netSalesPrice = parseInputValue(e.target.value);
    if (netSalesPrice === null) {
      setField("marginDollar", null);
      setField("marginPercent", null);
      return;
    }
    const { marginDollar, marginPercent } = computeFromNetSalesPrice(
      netSalesPrice,
      cost
    );
    setField("marginDollar", marginDollar);
    setField("marginPercent", marginPercent);
  };

  const handleMarginPercentBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (editingField !== "marginPercent") return;
    setEditingField(null);

    const inputValue = parseInputValue(e.target.value);
    if (inputValue === null) return;
    const { netSalesPrice, marginDollar } = computeFromMarginPercent(
      inputValue,
      cost
    );
    setField("netSalesPrice", netSalesPrice);
    setField("marginDollar", marginDollar);
  };

  const handleMarginDollarBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (editingField !== "marginDollar") return;
    setEditingField(null);

    const inputValue = parseInputValue(e.target.value);
    if (inputValue === null) return;
    const { netSalesPrice, marginPercent } = computeFromMarginDollar(
      inputValue,
      cost
    );
    setField("netSalesPrice", netSalesPrice);
    setField("marginPercent", marginPercent);
  };

  useEffect(() => {
    if (
      netSalesPrice == null ||
      watched?.marginDollar !== undefined ||
      watched?.marginPercent !== undefined
    ) {
      return;
    }
    // calculate margins and set form data on load for print capabilities
    const { marginDollar, marginPercent } = computeFromNetSalesPrice(
      netSalesPrice,
      cost
    );
    setValue(
      `${basePath}.marginDollar` as Path<T>,
      marginDollar as PathValue<T, Path<T>>,
      { shouldValidate: true, shouldDirty: false }
    );
    setValue(
      `${basePath}.marginPercent` as Path<T>,
      marginPercent as PathValue<T, Path<T>>,
      { shouldValidate: true, shouldDirty: false }
    );
  }, []);

  return (
    <Box className={classes.materialRow}>
      <Typography
        variant={TYPOGRAPHY_VARIANTS.BODY2}
        className={classes.materialLabel}
        title={materialLabel}
      >
        {highlightMatch(materialLabel, search)}
      </Typography>
      <Box className={classes.materialFields}>
        <DisplayField label={primaryLabel} text={`$${listPrice.toFixed(2)}`} />
        <DisplayField
          label={modifierLabel}
          text={`${modifierValue?.toFixed(isMultiplier ? 2 : 0)}${
            !isMultiplier ? "%" : ""
          }`}
        />
        <DisplayField
          label="Pre-Tax Sales Price"
          text={`$${preTaxSalePrice.toFixed(2)}`}
        />
        <DisplayField label="Tax Rate" text={`${taxRate.toFixed(2)}%`} />
        <DisplayField
          label="Post-Tax Sales Price"
          text={`$${afterTaxSalePrice.toFixed(2)}`}
          last
        />
        <NumericFormat<MiControlledTextFieldProps>
          customInput={MiControlledTextField}
          name={`${basePath}.netSalesPrice` as Path<T>}
          formControl={formControl}
          label="Net Sales Price"
          value={netSalesPrice ?? ""}
          decimalScale={2}
          fixedDecimalScale
          prefix="$"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              placeholder: "--.--",
            },
          }}
          allowNegative
          disabled={disabled}
          sx={{
            maxWidth: 110,
          }}
          className={classes.editableField}
          onValueChange={({ floatValue }, sourceInfo) => {
            if (sourceInfo.source !== "event") return;
            setEditingField("netSalesPrice");
            setField("netSalesPrice", floatValue ?? null);
          }}
          onBlur={handleNetSalesPriceBlur}
        />
        <NumericFormat<MiControlledTextFieldProps>
          customInput={MiControlledTextField}
          name={`${basePath}.marginPercent` as Path<T>}
          formControl={formControl}
          label="Margin (%)"
          value={marginPercent ?? ""}
          decimalScale={2}
          fixedDecimalScale
          suffix="%"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              placeholder: "--",
            },
          }}
          allowNegative
          disabled={disabled}
          sx={{
            maxWidth: 92,
          }}
          className={classes.editableField}
          onValueChange={({ floatValue }, sourceInfo) => {
            if (sourceInfo.source !== "event") return;
            setEditingField("marginPercent");
            setField("marginPercent", floatValue ?? null);
          }}
          onBlur={handleMarginPercentBlur}
        />
        <NumericFormat<MiControlledTextFieldProps>
          customInput={MiControlledTextField}
          name={`${basePath}.marginDollar` as Path<T>}
          formControl={formControl}
          label="Margin ($)"
          value={marginDollar ?? ""}
          decimalScale={2}
          fixedDecimalScale
          prefix="$"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              placeholder: "--",
            },
          }}
          allowNegative
          disabled={disabled}
          sx={{
            maxWidth: 92,
          }}
          className={classes.editableField}
          onValueChange={({ floatValue }, sourceInfo) => {
            if (sourceInfo.source !== "event") return;
            setEditingField("marginDollar");
            setField("marginDollar", floatValue ?? null);
          }}
          onBlur={handleMarginDollarBlur}
        />
      </Box>
    </Box>
  );
}

const DisplayField = ({
  label,
  text,
  last = false,
}: {
  label: string;
  text: string;
  last?: boolean;
}) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.readonlyField} mr={last ? 2 : 0}>
      <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION} color="textSecondary">
        {label}
      </Typography>
      <Typography variant={TYPOGRAPHY_VARIANTS.BODY2}>{text}</Typography>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  materialRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 2),
    borderBottom: `1px solid ${theme.palette.grey.lightAccent}`,
    "&:last-of-type": {
      borderBottom: "none",
    },
  },
  materialLabel: {
    flexShrink: 0,
    width: 300,
    minWidth: 300,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  materialFields: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    flexShrink: 0,
  },
  readonlyField: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    minWidth: 70,
  },
  editableField: {
    width: 120,
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.background.paper,
    },
    "& input": {
      textAlign: "right",
    },
  },
}));

const highlightMatch = (text: string, search: string) => {
  if (!search) return text;
  const idx = text.toLowerCase().indexOf(search.toLowerCase().trim());
  if (idx === -1) return text;
  const trimEndLength = search.length - search.trimEnd().length;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ fontWeight: 700 }}>
        {text.slice(idx, idx + search.length - trimEndLength)}
      </span>
      {text.slice(idx + search.length - trimEndLength)}
    </>
  );
};

interface MaterialPricingRowProps<T extends FieldValues> {
  material: IMaterialsResponse;
  watched: IMaterialsResponse | undefined;
  modifierValue: number;
  valueType: "multiplier" | "markup";
  basePath: string;
  formControl: Control<T>;
  setValue: UseFormSetValue<T>;
  disabled: boolean;
  search?: string;
}
